import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useDataProvider, useNotify, useRefresh, useUpdate } from "ra-core";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { Ticket } from "@/modules/types";
import { getTicketStatusNotePrompt } from "@/modules/tickets/ticketStatusWorkflow";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FloatingTextField } from "@/components/ui/floating-field";
import { ticketStatusLabel } from "@/modules/tickets/ticketInboxConfig";
import { formatStatusChangeInternalNoteBody } from "@/modules/tickets/parseStatusChangeInternalNote";
import { maybeSendTicketCsatEmail } from "@/modules/tickets/maybeSendTicketCsatEmail";

export type TicketStatusChangeRequest = {
  ticket: Ticket;
  nextStatus: string;
};

type TicketStatusChangeDialogProps = {
  request: TicketStatusChangeRequest | null;
  onClose: () => void;
  onSuccess?: () => void;
  requireStatusNote?: boolean;
};

export const TicketStatusChangeDialog = ({
  request,
  onClose,
  onSuccess,
  requireStatusNote = true,
}: TicketStatusChangeDialogProps) => {
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const open = request != null;
  const fromStatus = request?.ticket.status ?? "";
  const toStatus = request?.nextStatus ?? "";

  useEffect(() => {
    if (open) setNote("");
  }, [open, request?.ticket.id, request?.nextStatus]);

  const handleOpenChange = (next: boolean) => {
    if (!next && !pending) {
      setNote("");
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!request) return;
    const trimmed = note.trim();
    if (requireStatusNote && !trimmed) {
      notify("Add an internal note before changing status", {
        type: "warning",
      });
      return;
    }

    setPending(true);
    try {
      if (trimmed) {
        await dataProvider.replyTicket({
          ticketId: request.ticket.id,
          body: formatStatusChangeInternalNoteBody(
            fromStatus,
            toStatus,
            trimmed,
          ),
          isInternalNote: true,
        });
      }
      await update(
        "tickets",
        {
          id: request.ticket.id,
          data: { status: request.nextStatus },
          previousData: request.ticket,
        },
        { mutationMode: "optimistic" },
      );
      if (request.nextStatus === "resolved") {
        void maybeSendTicketCsatEmail(dataProvider, request.ticket.id);
      }
      notify("Status updated", { type: "info" });
      setNote("");
      onClose();
      onSuccess?.();
      refresh();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not update status",
        { type: "error" },
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent onKeyDown={(event) => event.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Status change note</DialogTitle>
          <DialogDescription>
            {getTicketStatusNotePrompt(fromStatus, toStatus)}
          </DialogDescription>
        </DialogHeader>

        <FloatingTextField
          id="ticket-status-note"
          label={
            <>
              Internal note ·{" "}
              <span className="capitalize text-muted-foreground">
                {ticketStatusLabel(fromStatus)} → {ticketStatusLabel(toStatus)}
              </span>
            </>
          }
          value={note}
          onChange={setNote}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder="Explain what happened or what we are waiting for…"
          multiline
          rows={5}
          controlClassName="min-h-[120px]"
          disabled={pending}
          autoFocus
        />

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending || !note.trim()}
            onClick={() => void handleConfirm()}
          >
            {pending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
